export type ConsentChannel = "email" | "sms" | "voice";

export interface IdxConsentReceipt {
  source_event_id: string;
  channel: ConsentChannel;
  choice: "grant" | "decline";
  endpoint: string;
  source_id: string;
  mapping_version?: string;
  capture_surface: string;
  capture_method: "checkbox";
  source_url: string;
  event_effective_at: string;
  source_observed_at: string;
  copy_text: string;
  copy_sha256: string;
}

export interface SplitConsentCapture {
  email: string;
  phoneNumber: string;
  callsAndEmailConsent: boolean;
  textConsent: boolean;
  surface: string;
  sourceUrl: string;
  callsAndEmailCopy: string;
  textCopy: string;
}

export interface SplitConsentSnapshot {
  signature: string;
  receipts: IdxConsentReceipt[];
}

export interface IdxConsentProfile {
  capture_enabled: boolean;
  source_id: string;
  mapping_version: string;
  policy_status: string;
}

/** The server-owned profile selects capture scope; it does not approve grants. */
export const scopeIdxConsentReceipts = (
  receipts: IdxConsentReceipt[] | undefined,
  profile: IdxConsentProfile | null
): IdxConsentReceipt[] | undefined => {
  if (
    !receipts?.length ||
    profile?.capture_enabled !== true ||
    !profile.source_id?.trim() ||
    !profile.mapping_version?.trim()
  ) {
    return undefined;
  }
  return receipts.map((receipt) => ({
    ...receipt,
    source_id: profile.source_id,
    mapping_version: profile.mapping_version,
  }));
};

/** Resolve server capture scope before signup; a failed lookup must remain retryable. */
export const resolveIdxConsentReceipts = async (
  receipts: IdxConsentReceipt[] | undefined,
  organizationId: string,
  fetchProfile: (organizationId: string) => Promise<IdxConsentProfile>
): Promise<IdxConsentReceipt[] | undefined> => {
  if (!receipts?.length) return undefined;
  return scopeIdxConsentReceipts(receipts, await fetchProfile(organizationId));
};

const sha256 = async (text: string): Promise<string> => {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
};

/** Reads the actual words displayed beside the two checkboxes. */
export const readSplitConsentCopy = (form: HTMLFormElement): {
  callsAndEmailCopy: string;
  textCopy: string;
} => {
  const textCopy = form.querySelector<HTMLElement>(
    '[data-consent-copy="sms"]'
  )?.textContent;
  const callsAndEmailCopy = form.querySelector<HTMLElement>(
    '[data-consent-copy="calls-email"]'
  )?.textContent;
  if (!textCopy?.trim() || !callsAndEmailCopy?.trim()) {
    throw new Error("Cannot capture consent without the displayed disclosure");
  }
  return { textCopy, callsAndEmailCopy };
};

/** Reuse an event for a retry of the same submission; changes create a new one. */
export const captureSplitConsent = async (
  input: SplitConsentCapture,
  previous: SplitConsentSnapshot | null = null
): Promise<SplitConsentSnapshot> => {
  if (!input.email.trim() || !input.phoneNumber.trim()) {
    throw new Error("Cannot capture consent without both submitted endpoints");
  }
  if (!input.callsAndEmailCopy.trim() || !input.textCopy.trim()) {
    throw new Error("Cannot capture consent without the displayed disclosure");
  }

  const signature = JSON.stringify(input);
  if (previous?.signature === signature) return previous;

  const source_event_id = crypto.randomUUID();
  const capturedAt = new Date().toISOString();
  const [callsAndEmailHash, textHash] = await Promise.all([
    sha256(input.callsAndEmailCopy),
    sha256(input.textCopy),
  ]);
  const common = {
    source_event_id,
    source_id: "idx_search_experience" as const,
    capture_surface: input.surface,
    capture_method: "checkbox" as const,
    source_url: input.sourceUrl,
    event_effective_at: capturedAt,
    source_observed_at: capturedAt,
  };
  return {
    signature,
    receipts: [
      {
        ...common,
        channel: "sms",
        choice: input.textConsent ? "grant" : "decline",
        endpoint: input.phoneNumber,
        copy_text: input.textCopy,
        copy_sha256: textHash,
      },
      {
        ...common,
        channel: "email",
        choice: input.callsAndEmailConsent ? "grant" : "decline",
        endpoint: input.email,
        copy_text: input.callsAndEmailCopy,
        copy_sha256: callsAndEmailHash,
      },
      {
        ...common,
        channel: "voice",
        choice: input.callsAndEmailConsent ? "grant" : "decline",
        endpoint: input.phoneNumber,
        copy_text: input.callsAndEmailCopy,
        copy_sha256: callsAndEmailHash,
      },
    ],
  };
};

export const captureDisplayedSplitConsent = (
  form: HTMLFormElement | null,
  input: Omit<SplitConsentCapture, "callsAndEmailCopy" | "textCopy">,
  previous: SplitConsentSnapshot | null = null
): Promise<SplitConsentSnapshot> => {
  if (!form) throw new Error("Cannot capture consent without the signup form");
  return captureSplitConsent({ ...input, ...readSplitConsentCopy(form) }, previous);
};
